import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Eyebrow, PageHero, Slate } from "../components/Bits";
import { DeckButton } from "../components/DeckButton";
import { SHOW, ARC, IMG } from "../data/show";

const FACTS = [
  ["Format", "Live-action sitcom, half-hour"],
  ["Setting", "A modern hilltop estate in Malibu, above the Pacific"],
  ["Star", "Samantha, a solid all-black cat"],
  ["Tone", "Fast-paced, rapidly escalating absurdity with heart"],
  ["Structure", "Cold open · Act One · Act Two · Act Three · Tag"],
  ["Season One", "10 episodes"],
];

const TONE = [
  { t: "Fast-Paced Comedy", d: "Sam never stops moving, so the edit doesn't either. Quick cuts, whip pans and hard outs keep the energy up." },
  { t: "Escalating Absurdity", d: "Every episode's domestic crisis is wilder than the last, and so is every act within an episode." },
  { t: "The Sideline Chorus", d: "RiRi and Jack stay unflappable, and their tiny reactions are the rhythm section of the show." },
  { t: "Deep Charm & Heart", d: "Every episode lands on a real moment. The dictator is also a sweetheart who looks after her family." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero testid="about-hero" eyebrow="About the Series" title="A Diva, a Hilltop, and the Pacific as Her Backdrop" sub={SHOW.logline} />
      <section className="section" data-testid="about-format-section">
        <div className="wrap grid lg:grid-cols-2 gap-14">
          <Reveal>
            <Eyebrow>Format</Eyebrow>
            <h2 className="display text-3xl md:text-5xl mt-3">The Show at a Glance</h2>
            <dl className="facts mt-10">
              {FACTS.map(([k, v]) => <div key={k} data-testid={`fact-${k.toLowerCase().replace(/\s/g, "-")}`}><dt className="slate">{k}</dt><dd>{v}</dd></div>)}
            </dl>
          </Reveal>
          <Reveal delay={0.1}>
            <Slate src={IMG.hero} label="Opening Visual / The Hilltop Estate" className="aspect-[3/2]" testid="about-opening-image" />
            <p className="mt-5 text-sm italic text-[var(--muted)]">{SHOW.openingVisual}</p>
          </Reveal>
        </div>
      </section>
      <section className="section bg-sand" data-testid="about-tone-section">
        <div className="wrap">
          <Reveal><Eyebrow>Tone & Narrative Style</Eyebrow><h2 className="display text-3xl md:text-5xl mt-3">How It Plays</h2></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {TONE.map((x, i) => (
              <Reveal key={x.t} delay={i * 0.08} className="preview-card" data-testid={`tone-${i}`}>
                <span className="arc-num text-4xl">0{i + 1}</span>
                <h3 className="display text-2xl mt-4">{x.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[var(--muted)]">{x.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section className="section" data-testid="about-vision-section">
        <div className="wrap grid lg:grid-cols-[7fr_5fr] gap-14 items-center">
          <Reveal>
            <Eyebrow>Creator's Vision</Eyebrow>
            <h2 className="display text-3xl md:text-5xl mt-3">Every Home Has a Star</h2>
            <p className="mt-6 leading-relaxed text-[var(--muted)]">Anyone who has lived with a cat like Samantha knows the truth: you don't own the house, you manage it. <em>Then Came Sam</em> takes that feeling and puts it on a Malibu hilltop with Hollywood-sized stakes. It's a workplace comedy where the boss has whiskers, the coworkers won't get up, and the only employee is the one who's sure he's in charge.</p>
            <p className="mt-4 leading-relaxed text-[var(--muted)]">It's a love letter to real pets with real personalities, including the dictator, the sweetheart, the unbothered sister and the sunbathing brother, and the people lucky enough to work for them.</p>
          </Reveal>
          <Reveal delay={0.1}><Slate src={IMG.pr} label="Samantha / Public Relations" className="aspect-[760/251]" testid="about-vision-image" /></Reveal>
        </div>
      </section>
      <section id="arc" className="section bg-ink text-white scroll-mt-16" data-testid="about-arc-section">
        <div className="wrap">
          <Reveal><Eyebrow light>Full Series Plot Arc</Eyebrow><h2 className="display text-3xl md:text-5xl mt-3">From House to Empire</h2></Reveal>
          <div className="mt-14 space-y-12">
            {ARC.map((a, i) => (
              <Reveal key={a.season} className="grid md:grid-cols-[180px_1fr] gap-6 border-t border-white/15 pt-10" data-testid={`about-arc-${i + 1}`}>
                <span className="arc-num text-[var(--sun)]">0{i + 1}</span>
                <div>
                  <p className="slate text-white/60">{a.season} · {a.tag}</p>
                  <h3 className="display text-3xl md:text-4xl mt-2">{a.title}</h3>
                  <p className="mt-4 text-white/75 leading-relaxed max-w-3xl">{a.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14"><div className="flex flex-wrap gap-4"><Link to="/episodes" className="btn btn-sun" data-testid="about-episodes-btn">See Season One <ArrowRight size={16} /></Link><DeckButton testid="about-download-deck-btn" className="btn btn-ghost" /></div></Reveal>
        </div>
      </section>
    </div>
  );
}
